import React from 'react';
import type { FC } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useServices } from '@/features/services/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { Button } from '@/components/ui/button';
import type { ServiceItem } from '@/services/api/services/servicesApi';

/**
 * HomePage displays the main landing page for AskiiMall Marketplace, following the Figma design.
 */
const CATEGORIES = [
  'All Categories',
  'Personal Productivity',
  'Work & Professional',
  'Shopping & Commerce',
  'Social & Digital',
];

const HomePage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get state from URL params
  const search = searchParams.get('search') || '';
  const selectedCategory = searchParams.get('category') || 'All Categories';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Update search state
  const setSearch = (newSearch: string) => {
    const params = new URLSearchParams(searchParams);
    if (newSearch) {
      params.set('search', newSearch);
    } else {
      params.delete('search');
    }
    params.delete('page'); // Reset to page 1 when searching
    setSearchParams(params);
  };

  // Update category state
  const setSelectedCategory = (newCategory: string) => {
    const params = new URLSearchParams(searchParams);
    if (newCategory !== 'All Categories') {
      params.set('category', newCategory);
    } else {
      params.delete('category');
    }
    params.delete('page'); // Reset to page 1 when changing category
    setSearchParams(params);
  };

  // Fetch services from API with backend filtering and pagination
  const { services, pagination, isLoading, error, refetch } = useServices({
    page: currentPage,
    limit: 20, // Set page limit to 20
    search: search || undefined, // Send search query to backend
    // TODO: Category filtering needs category UUIDs, not names
    // category: selectedCategory === 'All Categories' ? undefined : selectedCategory,
    sort: 'recent',
  });

      // Apply category filtering on frontend until we have category UUIDs
  const filteredServices = React.useMemo(() => {
    if (selectedCategory === 'All Categories') {
      return services;
    }
    return services.filter(service =>
      service.category?.name === selectedCategory
    );
  }, [services, selectedCategory]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    if (newPage > 1) {
      params.set('page', newPage.toString());
    } else {
      params.delete('page');
    }
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle loading state
  if (isLoading) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <LoadingSpinner size="lg" text="Loading services..." />
      </main>
    );
  }

  // Handle error state
  if (error) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4 text-lg">Failed to load services</p>
          <Button onClick={() => refetch()} variant="outline">
            Try Again
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col items-center bg-slate-50">
      {/* Hero Section */}
      <section className="w-full flex flex-col items-center pt-2 pb-12 bg-slate-50">
        <h1 className="text-5xl md:text-6xl font-extrabold text-center text-gray-900 mb-6">Supercharge Your AI</h1>
        <p className="text-lg md:text-lg text-center text-gray-500 max-w-2xl mb-8">
          Discover and integrate thousands of services to give your AI real-world capabilities
        </p>
        <SearchBar value={search} onChange={setSearch} />
      </section>

      {/* Categories Navigation */}
      <nav className="w-full flex justify-center gap-4 mb-12 flex-wrap bg-slate-50">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-5 py-2 rounded-full border transition ${
              selectedCategory === cat
                ? 'border-blue-500 text-blue-600 font-semibold bg-white shadow'
                : 'border-gray-200 text-gray-700 bg-white'
            }`}
            type="button"
          >
            {cat}
          </button>
        ))}
      </nav>

      {/* Services Grid */}
      <section className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 bg-slate-50">
        {filteredServices.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500 text-lg mb-4">
              {search || selectedCategory !== 'All Categories'
                ? 'No services found matching your criteria.'
                : 'No active services available at the moment.'
              }
            </p>
            {(search || selectedCategory !== 'All Categories') && (
              <Button
                onClick={() => {
                  setSearchParams(new URLSearchParams());
                }}
                variant="outline"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          filteredServices.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))
        )}
      </section>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="w-full max-w-6xl flex items-center justify-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              const pageNum = Math.max(1, Math.min(
                pagination.total_pages - 4,
                currentPage - 2
              )) + i;

              if (pageNum > pagination.total_pages) return null;

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === currentPage ? "default" : "outline"}
                  onClick={() => handlePageChange(pageNum)}
                  className="w-10 h-10"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!pagination.has_next}
          >
            Next
          </Button>
        </div>
      )}

      {/* Pagination Info */}
      {pagination && (
        <div className="w-full max-w-6xl text-center text-sm text-gray-500 mb-8">
          Showing {((currentPage - 1) * 20) + 1}-{Math.min(currentPage * 20, pagination.total)} of {pagination.total} services
        </div>
      )}
    </main>
  );
};

// Service Card Component
interface ServiceCardProps {
  service: ServiceItem;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service }) => {
  const navigate = useNavigate();
  const [iconError, setIconError] = React.useState(false);

  // Generate icon and background from service data
  const getServiceIcon = (service: ServiceItem): string => {
    const iconMap: Record<string, string> = {
      'weather': '☀️',
      'calendar': '📅',
      'stock': '💹',
      'note': '📝',
      'hotel': '🏨',
      'shop': '🛒',
      'flight': '✈️',
      'email': '📧',
      'project': '🗂️',
      'database': '💾',
      'migration': '🔄',
      'data': '📊',
    };

    const serviceName = service.name.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (serviceName.includes(key)) {
        return icon;
      }
    }
    return service.name.charAt(0).toUpperCase();
  };

  const getServiceIconBg = (categoryName: string | null): string => {
    if (!categoryName) return '#F3F4F6';

    const bgMap: Record<string, string> = {
      'Personal Productivity': '#EFF6FF',
      'Work & Professional': '#F0FDF4',
      'Shopping & Commerce': '#FFFBEB',
      'Social & Digital': 'linear-gradient(135deg, #FAF5FF 0%, #9333EA 100%)',
      'Weather & Climate': '#E0F2FE',
      'Document Processing': '#FEF3C7',
    };
    return bgMap[categoryName] || '#F3F4F6';
  };

  // Format rating from API response
  const formatRating = (service: ServiceItem): { rating: string; count: string } => {
    const rating = service.rating?.average?.toFixed(1) || '4.0';
    const count = service.rating?.count?.toString() || '0';
    return { rating, count };
  };

  const handleViewPlans = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/services/${service.id}?tab=pricing`);
  };

  const { rating, count } = formatRating(service);
  const icon = getServiceIcon(service);
  const iconBg = getServiceIconBg(service.category?.name || null);
  const showIconImage = service.icon_url && !iconError;

  return (
    <Link
      to={`/services/${service.id}`}
      className="bg-white rounded-2xl shadow flex flex-col p-6 hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500"
      tabIndex={0}
      aria-label={`View details for ${service.name}`}
    >
      <div className="flex items-center mb-4">
        {showIconImage ? (
          <img
            src={service.icon_url}
            alt={`${service.name} icon`}
            className="w-12 h-12 rounded-lg mr-4 object-contain"
            onError={() => setIconError(true)}
          />
        ) : (
        <div
          className="w-12 h-12 flex items-center justify-center rounded-lg mr-4"
          style={{ background: iconBg }}
        >
          <span className="text-2xl font-bold">{icon}</span>
        </div>
        )}
        <div>
          <div className="flex items-center gap-2">
          <h3 className="text-xl font-extrabold text-gray-900 leading-tight">{service.name}</h3>
            {service.is_free && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                Free
              </span>
            )}
          </div>
          <span className="text-base text-gray-400">by {service.company_name || 'Unknown'}</span>
        </div>
      </div>
      <p className="text-gray-500 text-base mb-4 flex-1 leading-relaxed">
        {service.description}
      </p>
      <div className="bg-slate-100 h-px w-full -mx-6 mb-0" />
      <div className="flex items-center justify-between mt-4 px-0">
        <div className="flex items-center gap-2">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20" style={{ color: STAR_COLOR }}>
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 0 0 .95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 0 0-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 0 0-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 0 0-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 0 0 .95-.69l1.286-3.967Z" />
          </svg>
          <span className="font-bold text-gray-900 text-lg">{rating}</span>
          <span className="text-gray-400 text-base">({count})</span>
        </div>
        <button
          onClick={handleViewPlans}
          className="text-blue-600 font-bold text-base hover:underline cursor-pointer"
          tabIndex={0}
          aria-label={`View pricing plans for ${service.name}`}
        >
          View Plans
        </button>
      </div>
    </Link>
  );
};

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ value, onChange }) => {
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <form onSubmit={handleSearch} className="relative w-full max-w-3xl">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Search services, tools..."
        className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-6 pr-12  text-gray-700 placeholder-gray-400 focus:outline-none shadow-sm"
        aria-label="Search services, tools"
      />
      <svg
        width="24"
        height="24"
        fill="none"
        viewBox="0 0 24 24"
        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      >
        <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z" />
      </svg>
    </form>
  );
}

// Custom star color for Figma
const STAR_COLOR = '#FFD600';

export default HomePage;
